import { useState, useEffect, useRef } from 'react'
import { searchEbookContents } from '../api/search'

const useContentSearch = (params = {}) => {
  // Search states
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const debounceRef = useRef(null)
  const queryRef = useRef(query)

  // Function to handle API errors
  const handleErrorResponse = async (e) => {
    console.error('Search error:', e)
    setError(e)
    setIsLoading(false)
  }

  // Function to process search results
  const handleSearchResults = (response) => {
    if (query !== queryRef.current) return // Ignore outdated queries

    const { results: newResults = [] } = response

    // Filter out non-media results
    const filteredResults = newResults.filter(
      (mediaItem) => mediaItem && (mediaItem.ebook || mediaItem.audiobook),
    )

    const allResults = filteredResults.flatMap((mediaItem) => {
      const { ebook, audiobook } = mediaItem
      if (ebook && audiobook) {
        return [
          { ...mediaItem, audiobook: undefined },
          { ...mediaItem, ebook: undefined },
        ]
      }
      return mediaItem
    })

    setResults(allResults)
    setIsLoading(false)
  }

  // Function to perform the search
  const handleSearch = async () => {
    if (!query || query.length < 3) {
      setResults([])
      setHasSearched(false)
      return
    }

    setIsLoading(true)
    setHasSearched(true) // User has initiated a search

    try {
      const response = await searchEbookContents({
        query,
        includeSkus: params.skus,
        content_set: 'library',
      })
      handleSearchResults(response)
    } catch (e) {
      handleErrorResponse(e)
    }
  }

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      queryRef.current = query
      handleSearch()
    }, 300)

    return () => clearTimeout(debounceRef.current) // Cleanup on unmount
    // eslint-disable-next-line
  }, [query])

  return {
    query,
    results,
    isLoading,
    hasSearched,
    error,
    setQuery,
  }
}

export default useContentSearch
